import React, { ReactNode } from 'react';

import { Link } from 'react-router-dom';

import { RightOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { TenantProductName, TenantUserPermission } from '@recurrency/core-api-schema/dist/common/enums';
import { notification } from 'antd';
import { theme } from 'theme';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { browserHistory } from 'utils/browserHistory';
import { hasUserPermission, isPurchaserRole, shouldShowProduct } from 'utils/roleAndTenant';
import { routes } from 'utils/routes';
import { track, TrackEvent } from 'utils/track';

export const linkablePhoneNumber = (phoneNumber: string | number, label?: ReactNode) => (
  <a href={`tel:${phoneNumber}`}>{label || phoneNumber}</a>
);

export const linkableEmail = (email: string, label?: ReactNode) => <a href={`mailto:${email}`}>{label || email}</a>;

export const linkableAddress = (address: string, label?: ReactNode) => (
  <ExternalLink to={`http://maps.google.com/?q=${encodeURIComponent(address)}`}>{label || address}</ExternalLink>
);

export const ButtonLink = (uri: string) => (
  <Link to={uri}>
    <RightOutlined />
  </Link>
);

export const ExternalLink = ({
  to,
  children,
  style,
  onClick,
}: {
  to: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
  onClick?: () => void;
}) => (
  <a
    href={to}
    style={style}
    target="_blank"
    rel="noopener noreferrer"
    onClick={() => {
      track(TrackEvent.ExternalLink_Click, {
        externalUrl: to,
      });
      onClick?.();
    }}
  >
    {children}
  </a>
);

/**
 * NotificationLink is needed instead of Link.
 * because antd creates a different ReactROM.render tree for notifications.
 * Since we only use a single history instance, NotificationLink references browserHistory.push
 * to prevent browser from refreshing page
 */
export const NotificationLink = ({
  to,
  children,
  style,
  notificationKey,
  onClick,
}: {
  to: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
  notificationKey?: string;
  onClick?: () => void;
}) => (
  <a
    href={to}
    style={style}
    onClick={(ev) => {
      ev.preventDefault();
      browserHistory.push(to);
      notification.close(notificationKey ?? to);
      if (onClick) {
        onClick();
      }
      return false;
    }}
  >
    {children}
  </a>
);

export const linkableText = ({ onClick, text }: { onClick: () => void; text: string }) => (
  <div
    className={css`
      color: ${theme.colors.info[600]};
      display: inline-block;
      cursor: pointer;
      &:hover {
        color: ${theme.colors.info[500]};
      }
    `}
    onClick={onClick}
  >
    {text}
  </div>
);

/**
 * This component is used so that we don't show a hyperlink to a salesSearchAndLookup page
 * when the tenant doesn't have the product. Otherwise they see a paywall screen for a product
 * we're not selling.
 */
export const SalesCustomerLink = ({ customerId, children }: { customerId: string; children: React.ReactNode }) => {
  const { activeTenant } = useGlobalApp();
  if (activeTenant && shouldShowProduct(activeTenant, TenantProductName.SalesSearchAndLookup)) {
    return <Link to={routes.sales.customerDetails(customerId)}>{children}</Link>;
  }
  return <>{children}</>;
};

/**
 * Same as SalesCustomerLink, but for the planning page, which requires the dfpaDemandPlanning
 * product as well as view access (matching the Planning entry in navModulesData). Unlike the others
 * the target is passed in, because deeplinks into Planning carry the filters of the linking page.
 */
export const PlanningLink = ({ to, children, target }: { to: string; children: React.ReactNode; target?: string }) => {
  const { activeTenant } = useGlobalApp();
  if (
    activeTenant &&
    shouldShowProduct(activeTenant, TenantProductName.DfpaDemandPlanning) &&
    hasUserPermission(activeTenant, TenantUserPermission.AccessPlanningPlanningView)
  ) {
    return (
      <Link to={to} target={target}>
        {children}
      </Link>
    );
  }
  return <>{children}</>;
};

/**
 * Same as SalesCustomerLink, but for the vendor details page, which requires the
 * dfpaSearchAndLookup product and a purchaser ERP role (matching the Vendors entry in
 * navModulesData).
 */
export const PurchasingVendorLink = ({ vendorId, children }: { vendorId: string; children: React.ReactNode }) => {
  const { activeTenant, activeErpRole } = useGlobalApp();
  if (
    activeTenant &&
    shouldShowProduct(activeTenant, TenantProductName.DfpaSearchAndLookup) &&
    isPurchaserRole(activeErpRole)
  ) {
    return <Link to={routes.purchasing.vendorDetails(vendorId)}>{children}</Link>;
  }
  return <>{children}</>;
};

/**
 * Same as SalesCustomerLink, but for the item details page. The purchasing item page requires the
 * dfpaSearchAndLookup product and a purchaser ERP role; the sales item page requires the
 * salesSearchAndLookup product with no role restriction (matching the Inventory entries in
 * navModulesData). The page is keyed by item code.
 */
export const ItemLink = ({ itemCode, children }: { itemCode: string; children: React.ReactNode }) => {
  const { activeTenant, activeErpRole } = useGlobalApp();
  // both item details routes require the Inventory view permission (see AppRoutes)
  if (activeTenant && hasUserPermission(activeTenant, TenantUserPermission.AccessPurchasingInventoryView)) {
    if (shouldShowProduct(activeTenant, TenantProductName.DfpaSearchAndLookup) && isPurchaserRole(activeErpRole)) {
      return <Link to={routes.purchasing.itemDetails(itemCode)}>{children}</Link>;
    }
    if (shouldShowProduct(activeTenant, TenantProductName.SalesSearchAndLookup)) {
      return <Link to={routes.sales.itemDetails(itemCode)}>{children}</Link>;
    }
  }
  return <>{children}</>;
};
